import React from 'react';
import { Eye, Monitor, Smartphone, Tablet } from 'lucide-react';
import type { PageLayout } from '../../../services/queries/layoutQueries';

interface LayoutPreviewProps {
  layout: PageLayout;
  objectName: string;
}

export const LayoutPreview: React.FC<LayoutPreviewProps> = ({ layout, objectName }) => {
  const [previewMode, setPreviewMode] = React.useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  const previewWidth = {
    desktop: 'w-full',
    tablet: 'w-[768px]',
    mobile: 'w-[375px]'
  };

  return (
    <div className="w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Live Preview</h3>
          </div>
        </div>

        {/* Preview Mode Selector */}
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setPreviewMode('desktop')}
            className={`flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              previewMode === 'desktop'
                ? 'bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <Monitor className="w-3 h-3" />
            Desktop
          </button>
          <button
            onClick={() => setPreviewMode('tablet')}
            className={`flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              previewMode === 'tablet'
                ? 'bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <Tablet className="w-3 h-3" />
            Tablet
          </button>
          <button
            onClick={() => setPreviewMode('mobile')}
            className={`flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              previewMode === 'mobile'
                ? 'bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <Smartphone className="w-3 h-3" />
            Mobile
          </button>
        </div>
      </div>

      {/* Preview Content */}
      <div className="flex-1 overflow-auto p-4 bg-gray-50 dark:bg-gray-900">
        <div className={`mx-auto transition-all duration-300 ${previewWidth[previewMode]}`}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Record Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                Sample {objectName}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Preview of how your layout will appear
              </p>
            </div>

            {/* Layout Sections */}
            <div className="p-4 space-y-4">
              {layout.sections.map(section => (
                <div key={section.id} className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 pb-2">
                    {section.label}
                  </h3>

                  <div className={`grid gap-4 ${section.columns === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {section.fields.map(field => (
                      <div key={field.fieldId} className="space-y-1">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                          {field.fieldLabel}
                          {field.required && <span className="text-red-600 dark:text-red-400 ml-1">*</span>}
                        </label>
                        <div className="px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-sm text-gray-400 dark:text-gray-500">
                          Sample value
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {layout.sections.length === 0 && (
                <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">
                  Add sections and fields to see preview
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer Info */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
          <div className="flex justify-between">
            <span>Total Sections:</span>
            <span className="font-semibold text-gray-900 dark:text-white">{layout.sections.length}</span>
          </div>
          <div className="flex justify-between">
            <span>Total Fields:</span>
            <span className="font-semibold text-gray-900 dark:text-white">
              {layout.sections.reduce((sum, s) => sum + s.fields.length, 0)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
